// ./src/pages/private/EventSchedulePage/SlotActsSummary.tsx
//
// The acts playing a date / session, READ-ONLY in its drawer: in playing order, each with its set time and stage (ROADMAP
// A2h #165 round 2, owner 2026-10-16: "the same act can be present on different dates but with different
// billing/image/position/price and current implementation on the time slot editor does not let do it so I am not sure that
// editor needs to let change acts there, but it [should] show acts in the order and times"). They are edited on the
// Artists page, on this date — the one button here goes there.
//
// ⚠ A read that failed says so (never "no acts yet"); a date not saved yet has no acts to show.
// ⚠ Rendered INSIDE the drawer: "Manage acts" leaves through the drawer's own question (`useDrawerLeave`), so unsaved
//   edits to the date are asked about, never dropped.

import React, { useEffect, useMemo, useState } from 'react';
import { Avatar, Button, Group, Input, Paper, Stack, Text } from '@mantine/core';
import { OpResult } from '@sdflc/api-helpers';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatEventDateTimeRange } from '@em/utils';

import { EventArtist, EventArtistService, WHOLE_SET, eventArtistDisplayName, eventArtistPhotoUrl } from '@/utils/api';
import { AppIcon, ErrorAlert, useDrawerLeave } from '@/components/generic';

export interface SlotActsSummaryProps {
  eventId: string;
  /** The date / session — null while it is being created. */
  slotId: string | null;
  slotStartsAt: string | null;
  timezone: string | null;
  isRecurring: boolean;
}

export const SlotActsSummary: React.FC<SlotActsSummaryProps> = ({ eventId, slotId, slotStartsAt, timezone, isRecurring }) => {
  const { t } = useTranslation(['events', 'common']);
  const navigate = useNavigate();
  const leave = useDrawerLeave();
  const [acts, setActs] = useState<EventArtist[]>([]);
  const [readDone, setReadDone] = useState(false);
  const [readError, setReadError] = useState<OpResult | null>(null);

  useEffect(() => {
    if (!slotId) {
      return undefined;
    }

    let alive = true;

    setReadDone(false);
    // ⓘ A date's lineup is read whole — it is drawn in playing order, which needs every act of it.
    EventArtistService.list({ eventId: [eventId], eventTimeSlotId: [slotId] }, { page: 1, pageSize: WHOLE_SET }).then((res) => {
      if (!alive) {
        return;
      }

      setReadError(res.didSucceed() ? null : res);
      setActs(res.didSucceed() && Array.isArray(res.data) ? (res.data as EventArtist[]) : []);
      setReadDone(true);
    });

    return () => {
      alive = false;
    };
  }, [eventId, slotId]);

  const rows = useMemo(() => {
    const setOf = (a: EventArtist) => a.performances?.find((p) => p.eventTimeSlotId === slotId) ?? null;
    const startOf = (a: EventArtist) => setOf(a)?.setStartsAt ?? slotStartsAt ?? '';

    return [...acts]
      .sort((a, b) => startOf(a).localeCompare(startOf(b)) || (a.billingIndex ?? 0) - (b.billingIndex ?? 0))
      .map((a) => {
        const set = setOf(a);

        return {
          a,
          when: set?.setStartsAt ? formatEventDateTimeRange(set.setStartsAt, set.setEndsAt ?? null, timezone) : null,
        };
      });
  }, [acts, slotId, slotStartsAt, timezone]);

  const manage = () => leave(() => navigate(`/events/${eventId}/artists?date=${encodeURIComponent(slotId ?? 'all')}`));

  return (
    <Input.Wrapper label={t('events:schedulePage.acts', { defaultValue: 'Acts' })} size="md">
      <Stack gap="xs" mt={4}>
        {!slotId ? (
          <Text size="md" c="dimmed">
            {isRecurring
              ? t('events:schedulePage.actsAfterSave', { defaultValue: 'Save this date, then book its acts on the Artists page.' })
              : t('events:schedulePage.actsAfterSaveSession', { defaultValue: 'Save this session, then book its acts on the Artists page.' })}
          </Text>
        ) : readError ? (
          <ErrorAlert error={readError} title={t('events:schedulePage.actsLoadFailed', { defaultValue: 'Could not load the acts' })} page="Schedule" operation="eventArtistList" />
        ) : readDone && rows.length === 0 ? (
          <Text size="md" c="dimmed">
            {t('events:schedulePage.actsNoneOnDate', { defaultValue: 'No acts on it yet.' })}
          </Text>
        ) : (
          rows.map(({ a, when }) => (
            <Paper key={a.id} withBorder radius="md" p="xs">
              <Group gap="sm" wrap="nowrap" align="flex-start">
                <Avatar src={eventArtistPhotoUrl(a)} radius="md" size={32} name={eventArtistDisplayName(a)} />
                <Stack gap={0} style={{ minWidth: 0 }}>
                  <Text size="md" fw={600} truncate>
                    {eventArtistDisplayName(a)}
                  </Text>
                  <Text size="md" c="dimmed">
                    {when ?? t('events:schedulePage.actOwnTime', { defaultValue: 'At the date’s own time' })}
                  </Text>
                </Stack>
              </Group>
            </Paper>
          ))
        )}
        {slotId && (
          <Group>
            <Button variant="light" size="md" leftSection={<AppIcon iconName="Microphone" size={16} />} onClick={manage}>
              {t('events:schedulePage.actsManage', { defaultValue: 'Manage acts on the Artists page' })}
            </Button>
          </Group>
        )}
      </Stack>
    </Input.Wrapper>
  );
};
